<script lang="ts" module>
  import { type FormType, type InputAttribute } from './fields.ts';

  const dateComponentRoles = ['month', 'day', 'year'] as const;
  type DateComponentRole = (typeof dateComponentRoles)[number];
  const dateComponentOffset = {
    year: 0,
    month: 5,
    day: 8,
  } as const satisfies Record<DateComponentRole, number>;

  export interface Props<F extends FormType> {
    form: DialogForm<F>;
    dict?: PromptDict | undefined;
    inputStyleType?: keyof typeof InputStyles;
    labelColor?: string | undefined;
    disabled?: boolean | undefined;
  }
</script>

<script lang="ts" generics="F extends FormType">
  import { arrayOf, includes } from '@yp/common/containers.ts';
  import { assertKeyOf, assertPresent, checkEqual } from '@yp/common/core.ts';
  import { focusAdjacent, type DomEvent } from '@yp/common/html.ts';
  import {
    isAbsent,
    isMemberOf,
    isPresent,
    pick,
    recordKeys,
    validate,
  } from '@yp/common/identity.ts';
  import { asNumber } from '@yp/common/math.ts';
  import type {
    SimpleValueType,
    StrictPropertyDescriptorOf,
    TypeDescriptor,
  } from '@yp/common/schema/descriptor.ts';
  import { propToLabel } from '@yp/common/strings.ts';
  import { createEventDispatcher } from 'svelte';
  import type { FormEventHandler, KeyboardEventHandler } from 'svelte/elements';
  import type { FormEvents } from '../forms/forms.ts';
  import { Icon } from '../gui/index.ts';
  import { type DialogForm, type PromptDict } from '../gui/modals.ts';
  import { PromptLinesDom } from '../gui/prompt_lines_dom.ts';
  import { classes } from '../gui/util.ts';
  import { InputAttributes, InputStyles, ToggleStyle, watchTextAreaHeight } from './fields.ts';

  const debug = false;

  let {
    form = $bindable(),
    dict = undefined,
    inputStyleType = 'overlap',
    labelColor = 'blue',
    disabled = undefined,
  }: Props<F> = $props();

  type Events = 'update';
  const dispatch = createEventDispatcher<FormEvents<typeof form.value, Events>>();
  const onEvent = (
    d: StrictPropertyDescriptorOf<F>,
    event: keyof FormEvents<typeof form.value, Events>,
    el?: HTMLElement,
  ) => dispatch(event, { field: d.k, el });

  const hidden = $derived(form.hidden ?? []);
  // $: console.log('FieldList', { value: form.value, hidden, errors: form.errors });

  const supported = [...recordKeys(InputAttributes), 'bool'] as const;

  const inputStyle = InputStyles[inputStyleType];
  const formFields = $state(
    arrayOf<HTMLInputElement | HTMLTextAreaElement | undefined>(form?.desc.length ?? 0, undefined),
  );

  export function focus(focused = true) {
    for (const ff of formFields) {
      if (isPresent(ff)) {
        if (focused) {
          if (ff.disabled || hidden.includes(ff.id)) {
            // console.log('FieldList focus skipping', ff.id);
            continue;
          }
          // console.log('FieldList focusing', ff.id);
          ff.focus({ preventScroll: true });
          // ff.select();
          return true;
        }
        if (ff === document.activeElement) {
          ff.blur();
          return true;
        }
      }
    }
    return false;
  }

  function inputType(d: StrictPropertyDescriptorOf<F>) {
    assertKeyOf(d.t, InputAttributes);
    const attr = InputAttributes[d.t] as InputAttribute<SimpleValueType>;
    return attr.type(d);
  }

  function toValue(d: StrictPropertyDescriptorOf<F>): string {
    assertKeyOf(d.t, InputAttributes);
    const attr = InputAttributes[d.t] as InputAttribute<SimpleValueType>;
    const value = form.value[d.k];
    const result = attr.toInput(value, d);
    if (debug) {
      console.log('toValue', { ...pick(d, 'k', 't'), value, result });
    }
    return result;
  }

  function toComponentValue(d: StrictPropertyDescriptorOf<F>, role: DateComponentRole): string {
    const value = toValue(d);
    let result = value.slice(
      dateComponentOffset[role],
      dateComponentOffset[role] + (role === 'year' ? 4 : 2),
    );
    if (asNumber(result) === 0) {
      result = '';
    }
    if (debug) {
      console.log('toComponentValue', { ...pick(d, 'k', 't'), value, result });
    }
    return result;
  }

  function handleInput(
    d: StrictPropertyDescriptorOf<F>,
    el: HTMLInputElement | HTMLTextAreaElement,
  ) {
    const empty = el.value.trim().length === 0;

    assertKeyOf(d.t, InputAttributes);
    const attr = InputAttributes[d.t] as InputAttribute<SimpleValueType>;
    const parsed = empty ? undefined : (attr.fromInput(el.value, d) as F[typeof d.k] | undefined);

    if (debug) {
      console.log('onInput', {
        ...pick(d, 'k', 't'),
        value: form.value[d.k],
        input: el.value,
        parsed,
        empty,
      });
    }

    // Don't update form values when the field doesn't parse.
    if (parsed !== form.value[d.k] && (parsed !== undefined || empty)) {
      if (debug) {
        console.log('onInput update');
      }
      form.value[d.k] = parsed;
      if (debug) {
        console.log('onInput updated', { value: form.value[d.k] });
      }
      // TODO: This is wrong. valid only resets when we change form.value. Need shadow form.
      onEvent(d, 'update', el);
    }
  }

  function onInput(
    d: StrictPropertyDescriptorOf<F>,
  ): FormEventHandler<HTMLInputElement | HTMLTextAreaElement> {
    return (e: DomEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      assertPresent(form);
      handleInput(d, e.currentTarget);
    };
  }

  function onComponentKeydown(): KeyboardEventHandler<HTMLInputElement> {
    return (e: KeyboardEvent) => {
      const target = e.currentTarget as HTMLInputElement;
      if (e.key === 'Backspace' && target.value.length === 0) {
        focusAdjacent(target, 'prev');
      } else if (e.key === '/') {
        focusAdjacent(target, 'next');
        e.preventDefault();
      }
    };
  }

  function onComponentInput(d: StrictPropertyDescriptorOf<F>): FormEventHandler<HTMLInputElement> {
    return (e: DomEvent<HTMLInputElement>) => {
      assertPresent(form);

      const target = e.currentTarget;
      const maxLength = asNumber(target.getAttribute('maxlength'));
      const role = validate(target.getAttribute('data-role'), isMemberOf(dateComponentRoles));
      if (isAbsent(maxLength) || isAbsent(role)) {
        return;
      }

      const el = document.getElementById(d.k) as HTMLInputElement;
      const edited =
        el.value.slice(0, dateComponentOffset[role]) +
        e.currentTarget.value.padStart(maxLength, '0') +
        el.value.slice(dateComponentOffset[role] + maxLength);
      if (debug) {
        console.log('onComponentInput', { before: el.value, after: edited });
      }

      el.value = edited;
      handleInput(d, el);

      if (target.value.length >= maxLength) {
        focusAdjacent(target, 'next');
      }
    };
  }

  function onToggleSwitch(d: TypeDescriptor<boolean>): FormEventHandler<HTMLButtonElement> {
    checkEqual(d.t, 'bool');
    const attr = InputAttributes.bool;
    return (e: DomEvent<HTMLButtonElement>) => {
      e.preventDefault();
      assertPresent(form);
      const parsed = attr.fromInput(e.currentTarget.value);
      if (isPresent(parsed)) {
        form.value[d.k as keyof F] = !parsed as F[keyof F];
        onEvent(d, 'update', e.currentTarget);
      }
    };
  }
</script>

{#key hidden.join(' ')}
  {#each form.desc as d, i (d.k)}
    {#if !includes(supported, d.t)}
      <input type="hidden" name={d.k} id={d.k} value={toValue(d)} />
    {:else}
      {@const error = form.errors?.[d.k]}
      <div class={hidden.includes(d.k) ? 'hidden' : undefined}>
        <PromptLinesDom
          {dict}
          lines={form.prompts?.[d.k]}
          class="block my-4 text-sm font-medium text-gray-500" />
        {#if d.t === 'bool'}
          <div class={ToggleStyle.div}>
            <input type="hidden" {disabled} name={d.k} id={d.k} value={toValue(d) === 'true'} />
            <button
              {disabled}
              name={d.k}
              id={d.k}
              value={toValue(d)}
              role="switch"
              aria-checked={toValue(d) === 'true'}
              aria-labelledby={`${d.k}-label`}
              class={ToggleStyle.input}
              style={toValue(d) === 'true' ? `background-color: ${labelColor}` : undefined}
              onclick={onToggleSwitch(d)}>
              <span
                aria-hidden="true"
                class={`${ToggleStyle.buttonSpan} ${toValue(d) === 'true' ? 'translate-x-5' : 'translate-x-0'}`}
              ></span>
            </button>
            <label
              id={`${d.k}-label`}
              for={d.k}
              class={ToggleStyle.label}
              style={`color: ${labelColor}`}>{d.label ?? propToLabel(d.k)}</label>
          </div>
        {:else if inputType(d).type.startsWith('date')}
          <div
            class={classes(
              inputStyle.div,
              inputStyle.border,
              isPresent(error) ? '!ring-red-300' : undefined,
              'relative flex justify-start px-2 text-gray-900',
            )}>
            <label
              for={d.k}
              class={classes(inputStyle.label, isPresent(error) ? '!text-red-600' : undefined)}
              style={`color: ${labelColor}`}>{d.label ?? propToLabel(d.k)}</label>
            <input
              type="hidden"
              {disabled}
              name={d.k}
              id={d.k}
              value={toValue(d)}
              placeholder={form.placeholders?.[d.k]}
              oninput={onInput(d)}
              bind:this={formFields[i]}
              class={classes(
                inputStyle.input,
                inputStyle.border,
                isPresent(error) ? '!ring-red-300' : undefined,
              )} />
            <input
              type="text"
              inputmode="numeric"
              data-role="month"
              min="1"
              max="12"
              maxlength="2"
              placeholder="mm"
              disabled={(disabled ?? false) || inputType(d).type === 'date-year'}
              value={toComponentValue(d, 'month')}
              oninput={onComponentInput(d)}
              onkeydown={onComponentKeydown()}
              class={classes(inputStyle.input, inputStyle.datepart, '!w-[2em] text-right')} />
            <span
              class={classes(
                'px-1',
                inputType(d).type === 'date-year' ? 'bg-gray-200 text-gray-500' : undefined,
              )}>/</span>
            <input
              type="text"
              inputmode="numeric"
              data-role="day"
              min="1"
              max="31"
              maxlength="2"
              size="2"
              placeholder="dd"
              disabled={(disabled ?? false) || inputType(d).type === 'date-year'}
              value={toComponentValue(d, 'day')}
              oninput={onComponentInput(d)}
              onkeydown={onComponentKeydown()}
              class={classes(inputStyle.input, inputStyle.datepart, '!w-[2ch] text-right')} />
            <span
              class={classes(
                'pl-1',
                inputType(d).type === 'date-year' ? 'bg-gray-200 text-gray-500' : undefined,
              )}>/</span>
            <input
              type="text"
              inputmode="numeric"
              data-role="year"
              min="1900"
              max="2099"
              maxlength="4"
              placeholder="yyyy"
              disabled={(disabled ?? false) || inputType(d).type === 'date-calendar'}
              value={toComponentValue(d, 'year')}
              oninput={onComponentInput(d)}
              onkeydown={onComponentKeydown()}
              class={classes('pl-1', inputStyle.input, inputStyle.datepart)} />
            {#if isPresent(error)}
              <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <Icon icon="error" class="text-red-600" />
              </div>
            {/if}
          </div>
        {:else if inputType(d).type === 'textarea'}
          <div class={inputStyle.div}>
            <label
              for={d.k}
              class={classes(inputStyle.label, isPresent(error) ? '!text-red-600' : undefined)}
              style={`color: ${labelColor}`}>{d.label ?? propToLabel(d.k)}</label>
            <textarea
              {disabled}
              name={d.k}
              id={d.k}
              value={toValue(d)}
              placeholder={form.placeholders?.[d.k]}
              oninput={onInput(d)}
              bind:this={formFields[i]}
              class={classes(inputStyle.textarea, isPresent(error) ? '!ring-red-300' : undefined)}
              use:watchTextAreaHeight={toValue(d)}></textarea>
            {#if isPresent(error)}
              <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <Icon icon="error" class="text-red-600" />
              </div>
            {/if}
          </div>
          {#if isPresent(error)}
            <p class="mt-2 text-sm text-red-600" id={`${d.k}-error`}>{error}</p>
          {/if}
        {:else}
          <div class={inputStyle.div}>
            <label
              for={d.k}
              class={classes(inputStyle.label, isPresent(error) ? '!text-red-600' : undefined)}
              style={`color: ${labelColor}`}>{d.label ?? propToLabel(d.k)}</label>
            <input
              type={inputType(d).type}
              inputmode={inputType(d).inputmode}
              pattern={inputType(d).pattern}
              {disabled}
              name={d.k}
              id={d.k}
              value={toValue(d)}
              placeholder={form.placeholders?.[d.k]}
              oninput={onInput(d)}
              bind:this={formFields[i]}
              class={classes(
                inputStyle.input,
                inputStyle.border,
                isPresent(error) ? '!ring-red-300' : undefined,
              )} />
            {#if isPresent(error)}
              <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <Icon icon="error" class="text-red-600" />
              </div>
            {/if}
          </div>
          {#if isPresent(error)}
            <p class="mt-2 text-sm text-red-600" id={`${d.k}-error`}>{error}</p>
          {/if}
        {/if}
      </div>
    {/if}
  {/each}
{/key}

<style lang="scss">
  // Disallowed by Chrome: https://developer.mozilla.org/en-US/docs/Web/CSS/:autofill
  /* input:autofill {
  background-color: red !important;
} */

  // https://developer.mozilla.org/en-US/docs/Web/CSS/:autofill
  /* @-webkit-keyframes autofill {
  0%,
  100% {
    color: #666;
    background: transparent;
  }
} */

  :autofill,
  :-webkit-autofill {
    -webkit-background-clip: text;
    -webkit-text-fill-color: rgb(17, 24, 39) !important; // text-gray-900
    /* -webkit-animation-delay: 1s;
  -webkit-animation-name: autofill;
  -webkit-animation-fill-mode: both; */
  }
</style>
